'use client'

import { useEffect } from 'react'

/**
 * Mounted once in the root layout.  Hooks the browser's two global
 * error channels (window.onerror + unhandledrejection) and ships any
 * uncaught exception to /api/log/error so it lands in our error_log
 * Postgres table for backstage triage.
 *
 * Best-effort: failures inside the reporter are swallowed.  We also
 * de-dupe identical messages within a 30-second window so a runaway
 * React error doesn't carpet-bomb the table.
 */
const RECENT = new Map<string, number>()
const DEDUP_MS = 30_000

// Third-party / browser-extension noise that isn't our code and isn't
// actionable — keep it out of error_log so the daily count stays meaningful.
const NOISE = /MetaMask|ethereum provider|web3|SCDynimacBridge|webkit\.messageHandlers|^Script error\.?$|chrome-extension:|moz-extension:|safari-(web-)?extension|The object can ?not be found here|evaluating '__|googletranslate|cancelled|AbortError/i

function report(level: 'error' | 'warn', message: string, stack: string | undefined) {
  if (!message) return
  // Drop content-free messages (e.g. a bare "Uncaught ") and known noise.
  if (message.replace(/^(Uncaught|Unhandled)?\s*(Error|TypeError)?:?\s*/i, '').trim().length < 3) return
  if (NOISE.test(message) || NOISE.test(stack ?? '')) return
  const key = `${level}:${message}`
  const now = Date.now()
  const last = RECENT.get(key) ?? 0
  if (now - last < DEDUP_MS) return
  RECENT.set(key, now)

  fetch('/api/log/error', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      level,
      source: 'client',
      message: message.slice(0, 2000),
      stack: stack?.slice(0, 8000),
      url: typeof window !== 'undefined' ? window.location.href : undefined,
    }),
    keepalive: true,
  }).catch(() => { /* swallow */ })
}

export function ClientErrorBoundary() {
  useEffect(() => {
    // A stale tab open across a deploy references old JS chunk hashes that
    // no longer exist → ChunkLoadError / "Importing a module script failed".
    // Reload once (guarded) so the user silently gets the new build instead
    // of a broken page. Returns true if it handled (recovered) the error.
    function recoverChunkError(text: string): boolean {
      if (!/ChunkLoadError|Loading chunk \S+ failed|Failed to (load|fetch).{0,30}chunk|error loading dynamically imported module|Importing a module script failed/i.test(text)) {
        return false
      }
      try {
        const KEY = 'rzr-chunk-reload-at'
        const last = Number(sessionStorage.getItem(KEY) || '0')
        if (Date.now() - last < 30_000) return true // already reloaded recently — don't loop
        sessionStorage.setItem(KEY, String(Date.now()))
        window.location.reload()
      } catch { /* ignore */ }
      return true
    }
    function onError(e: ErrorEvent) {
      // Ignore noisy ResizeObserver loop errors — they're benign Chrome
      // signals and would flood the log.
      if (e.message?.includes('ResizeObserver loop')) return
      if (recoverChunkError(`${e.message} ${e.error?.stack ?? ''}`)) return
      report('error', e.message ?? 'Unknown error', e.error?.stack)
    }
    function onRejection(e: PromiseRejectionEvent) {
      const reason = e.reason
      const msg = reason instanceof Error ? reason.message : typeof reason === 'string' ? reason : 'Unhandled promise rejection'
      const stack = reason instanceof Error ? reason.stack : undefined
      if (recoverChunkError(`${msg} ${stack ?? ''}`)) return
      report('error', msg, stack)
    }
    window.addEventListener('error', onError)
    window.addEventListener('unhandledrejection', onRejection)
    return () => {
      window.removeEventListener('error', onError)
      window.removeEventListener('unhandledrejection', onRejection)
    }
  }, [])
  return null
}
